import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import routes from './common/constants/routes';
import { selectUi } from './common/store/uiSlice';

// Layouts
import PrivateRoute from './common/components/PrivateRoute/PrivateRoute';
import { OnboardingLayout } from './common/layouts/Onboarding';

// Public pages
import Login from './Login';
import Logout from './Login/Logout';
import Impersonate from './Login/Impersonate';
import PasswordResetRouter from './PasswordReset';
import AdminRegistrationRouter from './Registration/Admin';
import Invitation from './Registration/Invitation';
import NotificationUnsubscribe from './NotificationUnsubscribe';
import Welcome from './Welcome';

// Private pages
import PollRouter from './Poll';
import UnitsRouter from './Units';
import BuildingRouter from './Building';
import FilesRouter from './FileStorage';
import FeedRouter from './Feed';
import Notifications from './Notifications';
import ProfileRouter from './Profile';
import Subscriptions from './Subscription';
import Registry from './Registry';
import Deadlines from './Deadlines';
import Issues from './Issues';
import OtherServices from './OtherServices';
import { AdminRoute } from './common/components/PrivateRoute/AdminRoute';

// Dev pages
import Playground from './Playground/Playground';
import { FlashMessageContainer } from './common/components';
import { useAppSelector } from './common/hooks';
import { PageError } from './System/PageError';

function App(): JSX.Element {
  const flashes = useAppSelector(selectUi).flashes;

  return (
    <Sentry.ErrorBoundary fallback={<PageError code="500" />}>
      <BrowserRouter basename="/">
        <Routes>
          <Route element={<OnboardingLayout />}>
            <Route path={`${routes.invitation}:token`} element={<Invitation />} />
            <Route path={`${routes.login}`} element={<Login />} />
            <Route path={`${routes.logout}`} element={<Logout />} />
            <Route path={`${routes.passwordReset}*`} element={<PasswordResetRouter />} />
            <Route path={`${routes.registration}*`} element={<AdminRegistrationRouter />} />
            {/* Odhlášení e-mailů monitoringu podepsaným odkazem z e-mailu – bez přihlášení */}
            <Route path={routes.notificationUnsubscribe} element={<NotificationUnsubscribe />} />
          </Route>

          {/* Převzetí tokenu z admin back-office (impersonate) – bez layoutu, jen redirect */}
          <Route path={routes.impersonate} element={<Impersonate />} />

          {/* Welcome stránka předregistrovaného SVJ – vlastní celostránkový layout (rozmazaná aplikace + modal) */}
          <Route path={`${routes.welcome}:token`} element={<Welcome />} />

          <Route element={<PrivateRoute />}>
            <Route path={`${routes.root}`} element={<Navigate to={routes.poll} />} />
            <Route path={`${routes.poll}*`} element={<PollRouter />} />
            <Route path={`${routes.feed}*`} element={<FeedRouter />} />
            <Route path={`${routes.units}*`} element={<UnitsRouter />} />
            <Route path={`${routes.building}*`} element={<BuildingRouter />} />
            <Route path={`${routes.files}*`} element={<FilesRouter />} />
            <Route path={`${routes.profile}*`} element={<ProfileRouter />} />
            <Route path={routes.notifications} element={<Notifications />} />
            <Route path={`${routes.subscriptionOrder}*`} element={<AdminRoute><Subscriptions /></AdminRoute>} />
            <Route path={routes.registry} element={<AdminRoute><Registry /></AdminRoute>} />
            <Route path={routes.deadlines} element={<AdminRoute><Deadlines /></AdminRoute>} />
            {/* Závady a podněty – pro všechny členy budovy (member i admin) */}
            <Route path={routes.issues} element={<Issues />} />

            <Route path={`${routes.otherServices}*`} element={<OtherServices />} />
          </Route>

          {import.meta.env.DEV && <Route path={`${routes.playground}*`} element={<Playground />} />}

          <Route path="*" element={<PageError code="404" />} />
        </Routes>
      </BrowserRouter>
      <FlashMessageContainer items={flashes} />
    </Sentry.ErrorBoundary>
  );
}

export default App;
